import React, { FC, memo, useCallback, useContext } from 'react'
import { ScreenContext } from 'react-components'
import { useTranslation } from '../../../hooks/locale'
import ListingSectionTransport from '../../../transports/common/listing-section-transport'
import GridCard from '../common/grid-card'
import {
    Container,
    InnerContainer,
    SliderContainer,
    SortOptionsContainer,
    AccommodationsGrid,
    MobileSortOverrides,
} from './styles'
import SortOption from './sort-option'
import { Slide } from 'pure-react-carousel'
import AnalyticsService from '../../../services/analytics-service'
import { landingPageEventCategories, cardEventCategories } from '../../../services/analytics-service/categories'
import { TopRecommendationsTransport } from '../../../transports/region-landing-page-transport'
import { CtaButtonText, CtaContainer, StyledGhostButton, Title, TitleContainer } from '../common/styles'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { useResponsiveCards } from '../../../hooks/responsive'
import { StyledCarouselProvider, StyledSlider } from '../../common/listing-card-section/styles'

interface TopRecommendedSectionProps {
    topRecommendedSection: ListingSectionTransport
    staticTopRecommendedSection: TopRecommendationsTransport
    url: string
    locationName: string
    offerHeading?: string
    breadcrumbList?: BreadcrumbListTransport
    sitePathname?: string
}

const viewAllAccommodationsButtonPosition = {
    TOP: 'top',
    BOTTOM: 'bottom',
}

const TopRecommendationsSection: FC<TopRecommendedSectionProps> = ({
    topRecommendedSection,
    staticTopRecommendedSection,
    breadcrumbList,
    url,
    sitePathname,
}) => {
    const { heading, objects, sortOptions, cta } = topRecommendedSection
    // TODO (Future): The backend search logic is currently returning more results than the specified limit of 8.
    // A temporary workaround has been added to enforce the limit. This needs to be properly fixed in the backend.
    const topRecommendationsObjects = objects.slice(0, 8)
    const { t } = useTranslation()
    const { isDesktop } = useContext(ScreenContext)

    const topRecommendedSectionHeading = staticTopRecommendedSection?.heading ?? heading

    const _handleViewAllAccommodationsClick = useCallback(
        (ctaPosition: string) => {
            const eventCategory =
                ctaPosition === viewAllAccommodationsButtonPosition.TOP
                    ? landingPageEventCategories.topRecommendationsSection.TOP_CTA_VIEW_RECOMMENDATIONS
                    : landingPageEventCategories.topRecommendationsSection.BOTTOM_CTA_VIEW_RECOMMENDATIONS

            if (cta?.url) {
                AnalyticsService.trackClickEvent(eventCategory, `${cta.url}`)
                document.location.href = cta.url
            }
        },
        [cta?.url],
    )

    const visibleSlides = useResponsiveCards()
    const dragStep = Math.floor(visibleSlides)

    return (
        <Container id="top-recommendation">
            <InnerContainer>
                <TitleContainer>
                    <Title>{topRecommendedSectionHeading}</Title>
                </TitleContainer>
                <MobileSortOverrides>
                    <SortOptionsContainer>
                        <SortOption sortOptions={sortOptions} />
                    </SortOptionsContainer>
                </MobileSortOverrides>
                {isDesktop ? (
                    <AccommodationsGrid>
                        {topRecommendationsObjects.map((recommendedObject, index) => (
                            <GridCard
                                key={recommendedObject.id}
                                listingObject={recommendedObject}
                                position={index}
                                breadcrumbList={breadcrumbList}
                                requestUrl={url}
                                sitePathname={sitePathname}
                                pageName={cardEventCategories.pages.REGION_LANDING_PAGE}
                                sectionName={cardEventCategories.sections.TOP_RECOMMENDATIONS}
                            />
                        ))}
                    </AccommodationsGrid>
                ) : (
                    <SliderContainer>
                        <StyledCarouselProvider
                            totalSlides={topRecommendationsObjects.length}
                            visibleSlides={visibleSlides}
                            naturalSlideWidth={1}
                            naturalSlideHeight={1}
                            isIntrinsicHeight
                            lockOnWindowScroll
                            dragStep={dragStep}
                            step={dragStep}
                        >
                            <StyledSlider $transitionTime={0.2}>
                                {topRecommendationsObjects.map((recommendedObject, index) => (
                                    <Slide index={recommendedObject.id} key={`${index}_${recommendedObject.id}`}>
                                        <GridCard
                                            listingObject={recommendedObject}
                                            position={index}
                                            breadcrumbList={breadcrumbList}
                                            requestUrl={url}
                                            sitePathname={sitePathname}
                                            pageName={cardEventCategories.pages.REGION_LANDING_PAGE}
                                            sectionName={cardEventCategories.sections.TOP_RECOMMENDATIONS}
                                        />
                                    </Slide>
                                ))}
                            </StyledSlider>
                        </StyledCarouselProvider>
                    </SliderContainer>
                )}
                {!!cta && (
                    <CtaContainer>
                        <StyledGhostButton
                            onClick={() =>
                                _handleViewAllAccommodationsClick(viewAllAccommodationsButtonPosition.BOTTOM)
                            }
                            borderRadius="1rem"
                        >
                            <CtaButtonText>{t('seeAll')}</CtaButtonText>
                        </StyledGhostButton>
                    </CtaContainer>
                )}
            </InnerContainer>
        </Container>
    )
}

export default memo(TopRecommendationsSection)
